import { CSSProperties, DragEvent, Key, ReactNode, useRef, useState } from 'react'
import { Base64File, Base64FileInput } from '../Base64FileInput/Base64FileInput'
import { readAsBase64File } from '../Base64FileInput/readAsBase64File'
import { Icon } from '../Icon/Icon'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { ClassName, cx } from '@/utils/cx'

interface FileUploadProps {
  onSelect: (file: Base64File) => void
  inputId?: string
  inputKey?: Key
  multiple?: boolean
  disabled?: boolean
  className?: ClassName
  icon?: ReactNode
  title?: ReactNode
  uploadButton?: ReactNode
  draggingLabel?: string
  selectedFile?: Base64File
  onDeselect?: () => void
  deselectAriaLabel?: string
  accept?: string
  allowedTypes?: string[]
  invalidTypeMessage?: string
  onTypeError?: (message: string) => void
}

export const FileUpload = ({
  onSelect,
  inputId = 'base64FileInput',
  inputKey,
  multiple = false,
  disabled = false,
  className,
  icon,
  title,
  uploadButton,
  draggingLabel = 'Drop here',
  selectedFile,
  onDeselect,
  deselectAriaLabel = 'Remove file',
  accept,
  allowedTypes,
  invalidTypeMessage = 'File type not allowed',
  onTypeError,
}: FileUploadProps) => {
  const zoneRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [lockedHeight, setLockedHeight] = useState<number>()
  const [typeError, setTypeError] = useState('')

  const dragDepth = useRef(0)

  const isTypeAllowed = (file: Base64File) =>
    !allowedTypes || allowedTypes.length === 0 || allowedTypes.includes(file.type)

  const stopDragging = () => {
    dragDepth.current = 0
    setIsDragging(false)
    setLockedHeight(undefined)
  }

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (dragDepth.current === 0) {
      setLockedHeight(zoneRef.current?.offsetHeight)
    }
    dragDepth.current += 1
    setIsDragging(true)
  }
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }
  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    dragDepth.current -= 1
    if (dragDepth.current <= 0) {
      stopDragging()
    }
  }

  const openPicker = () => {
    if (disabled) return
    document.getElementById(inputId)?.click()
  }

  const handleSelect = (file: Base64File) => {
    stopDragging()
    if (!isTypeAllowed(file)) {
      setTypeError(invalidTypeMessage)
      onTypeError?.(invalidTypeMessage)
      return
    }
    setTypeError('')
    onTypeError?.('')
    onSelect(file)
  }

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    stopDragging()
    if (disabled) return

    const dropped = event.dataTransfer?.files
    if (!dropped || dropped.length === 0) return

    const chosen = multiple ? Array.from(dropped) : [dropped[0]]
    try {
      const files = await Promise.all(chosen.map(readAsBase64File))
      const allowed = files.filter(isTypeAllowed)
      const dropTypeError = allowed.length < files.length ? invalidTypeMessage : ''
      setTypeError(dropTypeError)
      onTypeError?.(dropTypeError)
      allowed.forEach((file) => onSelect(file))
    } catch {
      // Ignore unreadable files.
    }
  }

  const style: CSSProperties = {}
  if (disabled) style.pointerEvents = 'none'
  if (isDragging && lockedHeight) {
    style.minHeight = lockedHeight
    style.justifyContent = 'center'
  }

  return (
    <>
      <div
        ref={zoneRef}
        className={cx(
          'generic__file-upload',
          selectedFile && 'generic__file-upload-has-file',
          className
        )}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        style={style}
        onKeyDown={(event) => onKeyDownHandler(event, openPicker)}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Base64FileInput
          key={inputKey}
          inputId={inputId}
          accept={accept}
          multiple={multiple}
          onChange={handleSelect}
        />

        {selectedFile ? (
          <div className="generic__file-info">
            <div className="generic__file-info__title">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.83333 3.45833C5.21201 3.45833 4.70833 3.96201 4.70833 4.58333V15.4167C4.70833 16.038 5.21201 16.5417 5.83333 16.5417H14.1667C14.788 16.5417 15.2917 16.038 15.2917 15.4167V8.45833H12.5C11.2804 8.45833 10.2917 7.46963 10.2917 6.25V3.45833H5.83333ZM11.375 3.53135V6.25C11.375 6.87132 11.8787 7.375 12.5 7.375H15.2187C15.1627 7.22747 15.0759 7.09191 14.9622 6.9782L11.7718 3.78784C11.6581 3.67412 11.5225 3.58726 11.375 3.53135ZM3.625 4.58333C3.625 3.3637 4.6137 2.375 5.83333 2.375H10.9763C11.562 2.375 12.1237 2.60766 12.5378 3.02181L15.7282 6.21216C16.1423 6.62631 16.375 7.188 16.375 7.77369V15.4167C16.375 16.6363 15.3863 17.625 14.1667 17.625H5.83333C4.6137 17.625 3.625 16.6363 3.625 15.4167V4.58333Z"
                  fill="#303438"
                />
              </svg>
              <span className="generic__file-info__name">{selectedFile.name}</span>
            </div>
            {onDeselect && (
              <button
                type="button"
                className="generic__file-deselect"
                aria-label={deselectAriaLabel}
                onClick={(event) => {
                  event.stopPropagation()
                  onDeselect()
                }}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <Icon type="trash" size="24px" color="#565B60" />
              </button>
            )}
          </div>
        ) : isDragging ? (
          <>
            <div className="generic__upload-icon">
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
              >
                <rect width="48" height="48" rx="10" fill="white" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M24 9C15.7157 9 9 15.7157 9 24C9 32.2843 15.7157 39 24 39C32.2843 39 39 32.2843 39 24C39 15.7157 32.2843 9 24 9ZM11 24C11 16.8203 16.8203 11 24 11C31.1797 11 37 16.8203 37 24C37 31.1797 31.1797 37 24 37C16.8203 37 11 31.1797 11 24Z"
                  fill="var(--icon-main-color, black)"
                />
                <rect
                  x="23"
                  y="17"
                  width="2"
                  height="14"
                  rx="1"
                  fill="var(--icon-main-color, black)"
                />
                <rect
                  x="31"
                  y="23"
                  width="2"
                  height="14"
                  rx="1"
                  transform="rotate(90 31 23)"
                  fill="var(--icon-main-color, black)"
                />
              </svg>
            </div>
            <div className="generic__upload-title">{draggingLabel}</div>
          </>
        ) : (
          <>
            {icon}
            {title}
            {uploadButton}
          </>
        )}
      </div>
      {!onTypeError && typeError && <div className="generic__file-error">{typeError}</div>}
    </>
  )
}
